import React from "react";

import {
  IgCodeEditor,
  IgToggleButtonGroup,
  IgTypography,
} from "@intelligems/ig-theme";
import { InjectionMod } from "@src/store/slices/experienceBuilder/types";
import { Box, ToggleButton, useTheme } from "@mui/material";
import { ModificationHeader } from "@src/features/experienceBuilder/modifications/common/ModificationHeader";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { useDispatch, useSelector } from "@src/store";
import {
  ExperienceBuilderAction as Action,
  ExperienceBuilderSelector as Selector,
} from "@src/store/slices/experienceBuilder";
import { assert } from "@src/utils/assert";

const buttons = [
  <ToggleButton value="css" key="customCss" size="small" sx={{ flex: 1 }}>
    CSS
  </ToggleButton>,
  <ToggleButton value="js" key="customJs" size="small" sx={{ flex: 1 }}>
    JavaScript
  </ToggleButton>,
];

export const InjectionPanel = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const errorColor = theme.palette.text.tonal.error;

  const mod = useSelector(Selector.basicMod.getVariationDraftInjection);
  assert(mod !== undefined, "mod is undefined");
  const isApplicable = useSelector(Selector.isVariationDraftApplicable);
  const errors = useSelector(Selector.getVariationDraftUIErrors);

  const update = (update: Partial<InjectionMod>) =>
    dispatch(Action.basicMod.setDraft({ ...mod, ...update }));
  const cancel = () => void dispatch(Action.cancelDraft({ trackCancel: true }));
  const apply = () => void dispatch(Action.applyDraft());

  return (
    <>
      <ModificationHeader />
      <Panel>
        <Panel.Body>
          <IgTypography variant="body1" fontWeight={600}>
            Edit CSS/JS
          </IgTypography>

          <IgToggleButtonGroup
            buttons={buttons}
            value={mod.tab}
            sx={{ width: "100%", flexShrink: 0 }}
            control={{
              exclusive: true,
              onChange: (_event, value: InjectionMod["tab"]) => {
                if (value) {
                  update({ tab: value });
                }
              },
            }}
          />
          {mod.tab === "js" && (
            <Box
              sx={{
                border: `1px solid ${theme.igBorders.core.default}`,
                borderRadius: "8px",
              }}
            >
              <IgCodeEditor
                autoFocus
                language={"js"}
                height={"400px"}
                value={mod.js}
                onChange={(js) => update({ js })}
                outline={errors?.js ? `1px solid ${errorColor}` : undefined}
              />
            </Box>
          )}
          {mod.tab === "css" && (
            <Box
              sx={{
                border: `1px solid ${theme.igBorders.core.default}`,
                borderRadius: "8px",
              }}
            >
              <IgCodeEditor
                autoFocus
                language={"css"}
                height={"400px"}
                value={mod.css}
                outline={errors?.css ? `1px solid ${errorColor}` : undefined}
                onChange={(css) => update({ css })}
              />
            </Box>
          )}
        </Panel.Body>

        <Panel.Buttons>
          <Panel.CancelButton onClick={cancel} />
          <Panel.ApplyButton onClick={apply} disabled={!isApplicable} />
        </Panel.Buttons>
      </Panel>
    </>
  );
};
